import {
    Alignment, HorizontalAlignment, LogManager, ModelRow, Navigation, ProcessLock, StringUtil, Api
} from "@mcleod/core";
import {
    Button, ClickListener, DataSourceMode, Label, Layout, LayoutLoadErrorEvent, Panel, PanelProps, Snackbar, Step,
    StepEvent, Toast, ValidationResult
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { LMEGeneralSettings } from "@mcleod/general";
import { Carrier, CarrierOfferInfo, ModelMovement, OrderCreditValidator, Orders, OrderTabView } from "@mcleod/dispatch";
import { SendLoadTender } from "@mcleod/datafusion";
import { CarrierAssignment } from "./CarrierAssignment";
import { InitiateBrokerageTracking } from "./InitiateBrokerageTracking";
import { SendRateConfirmation } from "./SendRateConfirmation";
import { AutogenLayoutCarrierAssignmentStepper } from "./autogen/AutogenLayoutCarrierAssignmentStepper";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { applyTrackingSearchRequestContext } from "./tracking/TrackingSearchContextUtil";

export interface CarrierAssignmentStepperProps extends PanelProps {
    movementId: string,
    orderId: string,
    manifestId: string,
    isIntercompany?: boolean,
    carrierId?: string,
    snackbarTarget?: Panel,
    carrierAssignmentButton: Button,
    carrierOfferInfo?: CarrierOfferInfo,
    isOfferView?: boolean,
    onClose: (canceled: boolean, updatedData?: ModelRow) => void,
    doAfterCarrierAssignmentSearch: (layout: CarrierAssignment) => void;
    doAfterSlideIn: (layout: CarrierAssignment) => void;
}

const INDEX_ASSIGN = 0;
const INDEX_RATE_CON = 1;
const INDEX_TRACKING = 2;

const log = LogManager.getLogger("powerbroker/CarrierAssignmentStepper");

export class CarrierAssignmentStepper extends AutogenLayoutCarrierAssignmentStepper {
    private movementId: string;
    private orderId: string;
    private manifestId: string;
    private onClose: (canceled: boolean, updatedData?: ModelRow) => void;
    public doAfterCarrierAssignmentSearch: (layout: CarrierAssignment) => void;
    private manualTrackingRequired = true;
    private activeTrackingVendorsFound = false;
    private assignedToastPanel: Panel;
    private changingStep = false;
    private panelHeader: Panel;
    private _carrierAssignmentLayout: CarrierAssignment;
    private sendRateConLayout: SendRateConfirmation;
    private sendLoadTenderLayout: SendLoadTender;
    private initiateTrackingLayout: InitiateBrokerageTracking;
    private _isIntercompany: boolean;
    private _carrierId: string;
    private isAutoTenderInProgress = false; // Track auto tender process
    private manualTenderAlreadySent = false; // Prevent duplicate manual tender sends

    override onLoad() {
        this.stepper["panelHeaderContent"].setProps({ paddingLeft: 30, paddingRight: 30, marginBottom: 24 });
        this.stepper.addBeforeStepListener((event: StepEvent) => {
            this.doBeforeStep(event);
        });
    }

    doBeforeStep(event: StepEvent) {
        if (!this.changingStep && event.newIndex > event.oldIndex) {
            event.preventDefault();
        } else {
            this.doBeforeStepLayoutDisplay(event.newIndex);
        }
    }

    doBeforeStepLayoutDisplay(index: number) {
        if (this.parent?.["dismissAllSnackbars"])
            this.parent["dismissAllSnackbars"]();
        const layout = this.getStepLayout(index);
        if (layout == null) return;

        if (layout.loadError == null) {
            layout.setAllDataSourcesToMode(DataSourceMode.UPDATE);
        }

        const panelHeaderContent = layout["panelHeader"] ?? this.createDefaultHeaderContent();
        if (this.panelHeader != null) {
            this.panelHeader.setProps({ padding: 0, marginBottom: 12 });
            this.panelHeader.removeAll();
            if (panelHeaderContent != null) {
                panelHeaderContent.visible = true;
                this.panelHeader.add(panelHeaderContent);
            }
        }
    }

    private createDefaultHeaderContent(onClick: ClickListener = () => this.getNextStepClickHandler()): Panel {
        const panelContent = new Panel({
            align: HorizontalAlignment.RIGHT,
            height: 54,
            fillRow: true,
            backgroundColor: "primary.darker",
            borderTopWidth: 4,
            borderTopColor: "primary.light"
        });

        const nextButton = new Button({
            caption: "Next",
            backgroundColor: "primary",
            color: "primary.reverse",
            fontBold: true,
            width: 160,
            borderWidth: 0,
            onClick
        });
        panelContent.add(nextButton);
        return panelContent;
    }

    private getNextStepClickHandler() {
        const currentIndex = this.stepper.selectedIndex;
        if (currentIndex === INDEX_RATE_CON) {
            // Check if we're in a tender layout or rate con layout
            if (this.sendLoadTenderLayout && this.getStepLayout(INDEX_RATE_CON) === this.sendLoadTenderLayout) {
                this.doAfterTenderSentStep();
            } else if (this.sendRateConLayout && this.getStepLayout(INDEX_RATE_CON) === this.sendRateConLayout) {
                this.doAfterRateConStep();
            } else {
                this.advanceToNextStep();
            }
        } else {
            this.advanceToNextStep();
        }
    }

    private getStepLayout(index: number) {
        return this.getStep(index).components[0] as Layout;
    }

    private getStep(index: number): Step {
        return this.stepper.components[index] as Step
    }

    private setupCarrierAssignmentLayout(props: Partial<CarrierAssignmentStepperProps>): Promise<CarrierAssignment> {
        return new Promise((resolve, reject) => {
            if (this.carrierAssignmentLayout != null) {
                resolve(this.carrierAssignmentLayout);
                return;
            }

            const layout = this.createCarrierAssignmentLayout(props, reject);
            layout.addLayoutLoadListener(() => {
                this.configureCarrierAssignmentDisplayListener(layout);
                this.searchAndConfigureCarrierAssignmentData(layout, resolve, reject);
            });
        });
    }

    private createCarrierAssignmentLayout(props: Partial<CarrierAssignmentStepperProps>, reject: (reason?: any) => void): CarrierAssignment {
        const layout = Layout.getLayout("lme/powerbroker/CarrierAssignment") as CarrierAssignment;
        layout.addLoadErrorListener((event: LayoutLoadErrorEvent) => reject(event.errorMessage));
        layout.carrierOfferInfo = props?.carrierOfferInfo;
        layout.isIntercompany = props?.isIntercompany;
        return layout;
    }

    private configureCarrierAssignmentDisplayListener(layout: CarrierAssignment): void {
        layout.sourceCarriersForAssignment.addDisplayListener((event) => {
            this.handleCarrierDisplayEvent(event, layout);
            this.configureIntercompanySettings(layout);
        });
    }

    private handleCarrierDisplayEvent(event: any, layout: CarrierAssignment): void {
        if (event.rowData == null && layout.mainDataSource.activeRow != null) {
            this.handleMovementTrackingConfiguration(layout);
        } else if (!this.manualTrackingRequired && this.activeTrackingVendorsFound) {
            this.handlePayeeTrackingConfiguration(layout);
        }
    }

    private handleMovementTrackingConfiguration(layout: CarrierAssignment): void {
        this.activeTrackingVendorsFound = layout.mainDataSource?.activeRow?.get("active_tracking_vendors_found", false);
        if (this.activeTrackingVendorsFound) {
            const manualTrackingRequiredOnMovement = layout.mainDataSource.activeRow?.getBoolean("manual_tracking_required", false);
            this.setManualTrackingRequired(manualTrackingRequiredOnMovement);
        } else {
            this.setManualTrackingRequired(false);
        }
    }

    private handlePayeeTrackingConfiguration(layout: CarrierAssignment): void {
        const trackingDisabledOnPayee: boolean = layout.sourceCarriersForAssignment?.activeRow?.get("disable_automatic_tracking", "N") === "Y";
        this.setManualTrackingRequired(trackingDisabledOnPayee);
    }

    private configureIntercompanySettings(layout: CarrierAssignment): void {
        if (this.isIntercompany) {
            this.updateStepCaptions();
            this.configureIntercompanyTextbox(layout);
            this.setManualTrackingRequired(false);
        }
    }

    private updateStepCaptions(): void {
        const rateConStep = this.stepper.findComponentById("sendRateConStep") as Step;
        rateConStep.caption = "Send Tender";
        const assignStep = this.stepper.findComponentById("carrierAssignmentStep") as Step;
        assignStep.caption = "Company Assignment";
    }

    private configureIntercompanyTextbox(layout: CarrierAssignment): void {
        layout.textboxPendingPayeeId.caption = "Shared Company";
        layout.textboxPendingPayeeId.lookupModel = "lme/powerbroker/carriers-for-assignment";
        layout.textboxPendingPayeeId.lookupModelLayout = "lme/datafusion/CompaniesForAssignment";
        layout.textboxPendingPayeeId.lookupModelDisplayField = "name";
        layout.textboxPendingPayeeId.lookupModelResultField = "id";
        layout.textboxPendingPayeeId.addBeforeLookupModelSearchListener(event => {
            event.filter.intercompany_list = this.isIntercompany;
        });
    }

    private searchAndConfigureCarrierAssignmentData(layout: CarrierAssignment, resolve: (value: CarrierAssignment) => void, reject: (reason?: any) => void): void {
        layout.mainDataSource.search({ "id": this.movementId, "orders.id": this.orderId }).then(result => {
            if (this.validateSearchResults(layout, reject)) {
                this.finalizeCarrierAssignmentSetup(layout, resolve);
                this.configureAssignmentButtonBehavior(layout);
                this.configurePostAssignmentHandling(layout);
            }
        });
    }

    private validateSearchResults(layout: CarrierAssignment, reject: (reason?: any) => void): boolean {
        if (layout.mainDataSource?.activeRow == null) {
            reject("Unable to find movement");
            return false;
        }

        const lockErrorMessage = layout.mainDataSource?.activeRow?.get("locked_error_message");
        if (lockErrorMessage != null) {
            reject(lockErrorMessage);
            return false;
        }

        return true;
    }

    private finalizeCarrierAssignmentSetup(layout: CarrierAssignment, resolve: (value: CarrierAssignment) => void): void {
        this.activeTrackingVendorsFound = layout.mainDataSource?.activeRow?.get("active_tracking_vendors_found", false);
        this.setManualTrackingRequired(layout.mainDataSource?.activeRow?.get("manual_tracking_required", false) == true);
        layout.doAfterClose = (canceled: boolean) => this.close(canceled);

        if (this.doAfterCarrierAssignmentSearch != null) {
            this.doAfterCarrierAssignmentSearch(layout);
        }

        this.getStep(INDEX_ASSIGN).add(layout);
        this.carrierAssignmentLayout = layout;
        resolve(layout);
    }

    private configureAssignmentButtonBehavior(layout: CarrierAssignment): void {
        const clickListeners = Object.assign([], layout.savebuttonAssign.getListeners("_clickListeners")?.listeners || []);

        // Remove existing listeners
        for (const listener of clickListeners) {
            layout.savebuttonAssign.removeClickListener(listener);
        }

        // Add custom click handler
        layout.savebuttonAssign.addClickListener(
            this.assignButtonClickHandler(layout, () => {
                for (const listener of clickListeners) {
                    layout.savebuttonAssign.addClickListener(listener);
                }
            })
        );
    }

    private configurePostAssignmentHandling(layout: CarrierAssignment): void {
        layout.mainDataSource.addAfterExecutionListener(async () => {
            if (!layout.activeRow?.isNull("override_payee_id")) {
                await this.handleCarrierAssignment(layout);
            } else {
                this.handleNoAssignment(layout);
            }
        });
    }

    private async handleCarrierAssignment(layout: CarrierAssignment): Promise<void> {
        this.createAssignedToastPanelIfNeeded(layout);
        const carrierId = layout.activeRow.get("override_payee_id");

        try {
            await this.processCarrierAssignment(carrierId);
        } catch (error) {
            log.error("Error handling carrier DF status:", error);
            Snackbar.showWarningSnackbar("Failed to process carrier assignment. Please try again.");
        }
    }

    private createAssignedToastPanelIfNeeded(layout: CarrierAssignment): void {
        if (window.location.pathname == "/lme/powerbroker/BrokeragePlanningMultiProfile" || window.location.pathname == "/lme/powerbroker/CarrierCapacity") {
            this.assignedToastPanel = this.getAssignedToastPanel(
                layout.mainDataSource.activeRow.get("override_payee_id"),
                layout.mainDataSource.activeRow.getFirstLookupModelData("override_payee_id").get("name")
            );
        }
    }

    private async processCarrierAssignment(carrierId: string): Promise<void> {
        const result = await CarrierAssignment.checkCarrierForDataFusion(carrierId, this.movementId);
        const carrierStatus = result.is_datafusion_carrier;
        const rcStep = this.stepper.findComponentById("sendRateConStep") as Step;

        if (this.isIntercompany) {
            this.setManualTrackingRequired(false);
        }

        if (carrierStatus === "auto") {
            await this.handleAutoDataFusionCarrier(carrierId, rcStep);
        } else if (carrierStatus === "manual") {
            await this.handleManualDataFusionCarrier(carrierId, rcStep);
        } else {
            await this.handleStandardCarrier(rcStep);
        }
    }

    private handleNoAssignment(layout: CarrierAssignment): void {
        const activeRow = layout.mainDataSource.activeRow;
        const pendingPayee = activeRow.get("pending_payee_id", activeRow.get("pending_carrier_nbr"));
        this.close(layout.origPendingPayeeId == null && pendingPayee == null, activeRow);
    }

    assignButtonClickHandler(layout: CarrierAssignment, callback: () => void) {
        let isProcessing = false;
        const button = layout.savebuttonAssign;
        return async () => {
            const validationResults:ValidationResult[] = layout.validate(true, true);
            const invalid = !ValidationUtil.checkValidationResults(validationResults);
            if (invalid) {
                ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
            }
            if (invalid || isProcessing || button.busy || !button.enabled) return;
            isProcessing = true;
            button.busy = true;
            try {
                await callback();
            } finally {
                isProcessing = false;
            }
        };
    }

    modifyLayoutAfterAssignment(layout: CarrierAssignment, keepButtonBusy: boolean = false) {
        // Skip if auto tender is in progress - let the auto tender handle layout modification at the right time
        if (this.isAutoTenderInProgress) {
            return;
        }

        layout.textboxPendingPayeeId.setProps({ printable: true, dataSource: null });
        const clickListeners = Object.assign([], layout.savebuttonAssign.getListeners("_clickListeners").listeners);
        for (const listener of clickListeners) {
            layout.savebuttonAssign.removeClickListener(listener);
        }
        layout.savebuttonAssign.setProps({
            caption: "Next",
            dataSource: null,
            enabled: !keepButtonBusy,
            busy: keepButtonBusy,
            busyWhenDataSourceBusy: false,
            onClick: () => this.advanceToNextStep()
        });
        layout.buttonCancel.setProps({ preventCollapse: true, visible: false });
        layout.carrierPayCallback = (hasErrors: boolean) => {
            if (!keepButtonBusy) {
                layout.savebuttonAssign.enabled = !hasErrors;
                if (!layout.savebuttonAssign.enabled)
                    layout.savebuttonAssign.disabledTooltip = "Errors must be resolved before advancing to next step";
            }
        };
    }

    private setupRateConLayout(): Promise<SendRateConfirmation> {
        return new Promise((resolve) => {
            if (this.sendRateConLayout != null) {
                if (this.sendRateConLayout.hasChangedData(this.carrierAssignmentLayout)) {
                    this.sendRateConLayout.search({
                        movement_id: this.movementId,
                        contact_name: this.carrierAssignmentLayout.activeRow.get("carrier_contact"),
                        email_address: this.carrierAssignmentLayout.activeRow.get("carrier_email")
                    }).then(() => {
                        resolve(this.sendRateConLayout);
                    });
                } else {
                    resolve(this.sendRateConLayout);
                }
            } else {
                const layout = Layout.getLayout("lme/powerbroker/SendRateConfirmation") as SendRateConfirmation;
                layout.addLoadErrorListener(() => {
                    this.getStep(INDEX_RATE_CON).add(layout);
                    this.sendRateConLayout = layout;
                    resolve(this.sendRateConLayout);
                });
                layout.addLayoutLoadListener(() => {
                    layout.search({
                        movement_id: this.movementId,
                        override_payee_id: this.carrierAssignmentLayout.activeRow.get("override_payee_id"),
                        contact_name: this.carrierAssignmentLayout.activeRow.get("carrier_contact"),
                        email_address: this.carrierAssignmentLayout.activeRow.get("carrier_email")
                    }).then(() => {
                        layout.doAfterRateConSent = () => {
                            // Automatically advance to next step after rate con is sent
                            this.doAfterRateConStep();
                        };
                        layout.buttonSendLater.visible = true;
                        layout.buttonCancel.visible = false;
                        const defaultTemplate = layout.activeRow.get("default_template");
                        const defaultEmail = layout.activeRow.get("default_email_address");
                        layout.buttonSend.enabled = !StringUtil.isEmptyString(defaultTemplate) && !StringUtil.isEmptyString(defaultEmail);
                        layout.buttonSend.caption = "Send Now";
                        layout.buttonSendLater.addClickListener(() => {
                            // Automatically advance to next step after clicking Send Later
                            this.doAfterRateConStep();
                        });
                        this.getStep(INDEX_RATE_CON).add(layout);
                        this.sendRateConLayout = layout;
                        resolve(this.sendRateConLayout);
                    });
                });
            }
        });
    }

    doAfterRateConStep(buttonSendLater?: Button) {
        if (buttonSendLater) buttonSendLater.busy = true;
        if (this.manualTrackingRequired) {
            this.setupTrackingLayout().then(() => {
                this.advanceToNextStep();
                if (buttonSendLater) buttonSendLater.busy = false;
            })
        } else {
            this.close(false);
        }
    }

    setupTrackingLayout(): Promise<InitiateBrokerageTracking> {
        return new Promise((resolve) => {
            if (this.initiateTrackingLayout != null) {
                resolve(this.initiateTrackingLayout);
            } else {
                const layout = Layout.getLayout("lme/powerbroker/InitiateBrokerageTracking") as InitiateBrokerageTracking;
                layout.addLoadErrorListener(() => {
                    this.getStep(INDEX_TRACKING).add(layout);
                    this.initiateTrackingLayout = layout;
                    resolve(layout);
                });
                layout.addLayoutLoadListener(() => {
                    const isManifest = !StringUtil.isEmptyString(this.manifestId);
                    const searchFilter = applyTrackingSearchRequestContext(layout, this.movementId, this.orderId, isManifest);
                    layout.search(searchFilter).then(() => {
                        layout.buttonInitiateLater.addClickListener(() => this.close(false));
                        layout.buttonInitiateLater.visible = true;
                        layout.buttonCancel.visible = false;
                        layout.buttonSave.caption = "Initiate Now";
                        layout.doAfterTrackingInitiated = () => this.close(false);
                        this.getStep(INDEX_TRACKING).add(layout);
                        this.initiateTrackingLayout = layout;
                        resolve(layout);
                    });
                });
            }
        });
    }

    advanceToNextStep() {
        const nextIndex = this.stepper.selectedIndex + 1;
        if (nextIndex >= this.stepper.components.length) {
            this.close(false);
        } else {
            this.changingStep = true;
            this.stepper.selectedIndex = nextIndex;
            this.changingStep = false;
        }
    }

    private setManualTrackingRequired(value: boolean) {
        if (this.manualTrackingRequired != value) {
            this.manualTrackingRequired = value;
            this.stepper.removeAt(INDEX_TRACKING);
        }
    }

    close(canceled: boolean, updatedData?: ModelRow) {
        this.parent.slideOut().then(() => {
            ProcessLock.releaseLock("Carrier dispatch", this.movementId);
            if (this.onClose)
                this.onClose(canceled);
            if (!canceled) {
                if (this.assignedToastPanel != null) {
                    Toast.showToast(this.assignedToastPanel, null, { millisUntilDismissal: 8000 });
                } else if (updatedData?.get("has_pending_assignment")) {
                    const pendingCarrierName = updatedData?.get("pending_carrier_name");
                    const pendingPayeeId = updatedData?.get("pending_payee_id");
                    let carrierName = pendingCarrierName;
                    if (pendingPayeeId != null) {
                        carrierName = pendingCarrierName ?? updatedData?.getFirstLookupModelData("pending_payee_id")?.get("name");
                    }

                    if (carrierName != null) {
                        const orderId = updatedData?.get("orders.id");
                        const msg = `A pending assignment has been created for order ${orderId} with carrier ${carrierName}`;
                        Toast.showToast(msg, null, { millisUntilDismissal: 8000 });
                    }
                }
            }
        });
    }

    getAssignedToastPanel(carrierID: string, carrierName: string, notes?: string): Panel {
        if (carrierID == null || carrierName == null) return null;
        const labelProps = {
            color: "white",
            fontSize: "large",
            rowBreak: false,
            paddingLeft: 1,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0
        }
        const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } }
        return new Panel({
            align: HorizontalAlignment.CENTER, fillRow: true,
            components: [
                new Label({ text: "Movement Assigned", fontSize: "xxlarge", fontBold: true, rowBreak: true }),
                new Label({ ...labelProps, text: StringUtil.isEmptyString(this.manifestId) ? "Order " : "Manifest " }),
                new Label({
                    ...labelLinkProps,
                    text: StringUtil.isEmptyString(this.manifestId) ? this.orderId : this.manifestId,
                    onClick: () => {
                        StringUtil.isEmptyString(this.manifestId) ? Orders.navigateTo(this.orderId) :
                            Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + this.manifestId, { newTab: true })
                    }
                }),
                new Label({ ...labelProps, text: " / Movement " }),
                new Label({
                    ...labelLinkProps,
                    text: this.movementId,
                    onClick: event => OrderTabView.navigateTo({
                        key: this.movementId,
                        orderId: this.orderId,
                        newTab: true
                    })
                }),
                new Label({ ...labelProps, text: " has been assigned to " }),
                new Label({ ...labelLinkProps, text: carrierName, onClick: event => Carrier.navigateTo(carrierID) })
            ]
        });
    }

    static async showAsSlideout(props: Partial<CarrierAssignmentStepperProps>) {
        const proceed: boolean = await new ModelMovement().searchSingle({ id: props.movementId }, "lme/dispatch/Movement").then((movementRow) => {
            if (movementRow.get("override_payee_id") != null) {
                Snackbar.showWarningSnackbar("This order has already been assigned to a carrier. Click the refresh button to see the latest updates.");
                return false;
            } else if ((props.isOfferView ?? true) && movementRow.get("pending_payee_id") != null) {
                Snackbar.showWarningSnackbar("A pending assignment is currently on this move so is not available for assignment", {targetPanel: props.snackbarTarget });
                return false;
            }
            else {
                return true;
            }
        });
        if (!proceed) return;
        ProcessLock.acquireLock("Carrier dispatch", props.movementId).then(lockResult => {
            if (lockResult.success === true) {
                const button = props.carrierAssignmentButton;
                const carrierOfferInfo = props?.carrierOfferInfo;
                const isIntercompany = props?.isIntercompany;
                const carrierId = props?.carrierId;

                if (button) button.busy = true;

                const panelHeader = new Panel({ padding: 0, fillRow: true });
                const panelWrapper = new Panel({
                    padding: 0, scrollY: false, id: "panelWrapper", height: "100%",
                    maxWidth: 1300, width: "75%", backgroundColor: "defaultBackground", top: 30,
                    borderRadius: 4, borderWidth: 1, borderShadow: true,
                    borderColor: "strokeSecondary", ...props
                });

                const carrierAssignmentStepper = Layout.getLayout("lme/powerbroker/CarrierAssignmentStepper", { ...props }) as CarrierAssignmentStepper;

                carrierAssignmentStepper.addLayoutLoadListener(() => {
                    carrierAssignmentStepper.setupCarrierAssignmentLayout(props).then((carrierAssignment) => {
                        const carrierRate = carrierOfferInfo?.carrier_rate;
                        const dispatchOffer = carrierOfferInfo?.dispatch_offer;
                        const counterOffer = carrierOfferInfo?.counter_offer;
                        const usingCarrierRate = !(counterOffer || dispatchOffer?.getAmount() != carrierRate?.getAmount());
                        carrierAssignmentStepper.panelHeader = panelHeader;
                        carrierAssignmentStepper.isIntercompany = isIntercompany;
                        carrierAssignmentStepper.carrierId = carrierId;
                        panelWrapper.add(panelHeader, carrierAssignmentStepper);
                        carrierAssignmentStepper.doBeforeStepLayoutDisplay(INDEX_ASSIGN);
                        const overlayProps = { closeOnClickOff: false, greyedBackground: true };
                        panelWrapper.slideIn({ direction: Alignment.RIGHT }, true, overlayProps).then(async () => {
                            if (props.doAfterSlideIn != null) {
                                props.doAfterSlideIn(carrierAssignment);
                            }
                            let carrierId = carrierAssignment.activeRow.get("pending_payee_id", carrierOfferInfo?.payee_id);
                            if (carrierOfferInfo?.payee_id != null &&  carrierOfferInfo?.payee_id != carrierAssignment.activeRow.get("pending_payee_id")) {
                                carrierId = carrierOfferInfo?.payee_id
                            }
                            if (carrierId != null) {
                                if (carrierAssignment.activeRow.getFirstLookupModelData("carrier_contact") == null ||
                                    carrierAssignment.activeRow.getFirstLookupModelData("carrier_contact").get("verification_status") == null) {
                                    await carrierAssignment.searchContact(carrierId);
                                }
                                else {
                                    carrierAssignment.validateCarrierForDispatch(carrierId);
                                }
                            }

                            if (!!carrierOfferInfo) {
                                if (carrierId != null) {
                                    carrierAssignment.activeRow.set("pending_payee_id", carrierId);
                                    carrierAssignment.sourceCarriersForAssignment.search({ "id": carrierId });
                                    carrierAssignment.activeRow.setLookupModelData("pending_payee_id", new ModelRow(carrierAssignment.textboxPendingPayeeId.lookupModel, false, {
                                        id: carrierId,
                                        name: carrierOfferInfo.carrier_name
                                    }));
                                    carrierAssignment.textboxPendingPayeeId.displayData(carrierAssignment.activeRow, null, 0);
                                    carrierAssignment.configureSaveButton();
                                } else {
                                    carrierAssignment.setPanelUnknownCarrierVisible(true, false);
                                    carrierAssignment.setPendingValuesFromOffer();
                                }
                                if (counterOffer) {
                                    carrierAssignment.activeRow.set("override_pay_rate", counterOffer.getAmount());
                                } else if (dispatchOffer && !usingCarrierRate) {
                                    carrierAssignment.activeRow.set("override_pay_rate", dispatchOffer.getAmount());
                                }
                            }

                            carrierAssignment.layoutMovementPay.defaultPayUnitsAndMethod();

                            carrierAssignment.layoutMovementPay.calculatePay(false,
                                LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y" && usingCarrierRate,
                                props.carrierOfferInfo != null, true);

                            if (carrierRate) {
                                if (counterOffer) {
                                    Snackbar.showWarningSnackbar("Offer has been accepted with a counter offer rather than the carrier rate matching this order");
                                } else if (carrierRate.getAmount() != dispatchOffer.getAmount()) {
                                    Snackbar.showWarningSnackbar("Offer has been accepted with a rate other than the carrier rate matching this order");
                                }
                            }
                            carrierAssignment.textboxPendingPayeeId.focus();
                            carrierAssignment.setDFLabel(carrierId, carrierAssignmentStepper.movementId);
                            OrderCreditValidator.validateCreditAndShowWarning(carrierAssignmentStepper.orderId);
                        });
                    }).catch(reason => {
                        CommonDialogs.showError(reason);
                        ProcessLock.releaseLock("Carrier dispatch", props.movementId);
                    }).finally(() => {
                        if (button) button.busy = false;
                    });
                });
            } else {
                CommonDialogs.showDialog(lockResult.message, { title: "Assignment in Progress" });
            }
        });

    }

    private setupLoadTenderLayout(carrierId: string, movementId: string, isAuto: boolean, autoBatch: string, wasSuccessful?: boolean, errorText?: string): Promise<SendLoadTender> {
        return new Promise((resolve) => {
            const layout = Layout.getLayout("lme/datafusion/SendLoadTender") as SendLoadTender;
            // For auto tenders, disable the layout's click listener to prevent duplicate sends
            // For manual tenders, we'll enable it later in the setup
            layout.useLayoutClickListener = false;
            // For auto tenders, prevent the layout from controlling button state
            layout.externalButtonControl = isAuto;
            layout.addLoadErrorListener(() => {
                this.getStep(INDEX_RATE_CON).add(layout);
                this.sendLoadTenderLayout = layout;
                resolve(this.sendLoadTenderLayout);
            });
            layout.addLayoutLoadListener(() => {
                const auto: string = isAuto ? "true" : "false";
                // Only use default error text if none was provided
                const displayErrorText = errorText || "Unable to transmit";
                layout.parentPanel = this.parent as Panel;
                layout.isIntercompany = this.isIntercompany;
                layout.search({
                    carrier: carrierId,
                    move_id: movementId,
                    is_cancel: "false",
                    is_auto: auto
                }).then(() => {
                    layout.doAfterLoadTenderSent = () => {
                        // Don't automatically advance - let user click Next to proceed
                    };
                    if (isAuto) {
                        // For auto tenders, the tender has already been sent
                        // Configure the layout to show results only, no sending capability
                        layout.useLayoutClickListener = false; // Ensure no accidental sends
                        layout.textboxMustRespondBy.printable = true;
                        layout.textboxMustRespondBy.fontSize = "large";
                        layout.textboxContact.printable = true;
                        layout.textboxContact.fontSize = "large";
                        layout.textboxAddContact.visible = false;
                        layout.buttonSendLater.visible = false;
                        layout.buttonSend.caption = this.manualTrackingRequired? "Next": "Close";
                        layout.labelPurpose.color = wasSuccessful ? "success" : "error";

                        // Disable data binding on the label and set text directly
                        layout.labelPurpose.dataSource = null;
                        const purposeText = wasSuccessful ? "ORIGINAL (Sent in batch " + autoBatch + ")" : "ORIGINAL (" + displayErrorText + ")";
                        layout.labelPurpose.text = purposeText;

                        // Set text after a brief delay to ensure layout initialization is complete
                        setTimeout(() => {
                            layout.labelPurpose.text = purposeText;
                        }, 100);

                        // For auto tenders, completely replace the send button functionality with Next
                        this.clearButtonListeners(layout.buttonSend);
                        layout.buttonSend.addClickListener(() => {
                            this.doAfterTenderSentStep();
                        });
                    } else {
                        // For manual tenders, keep the original caption and prepare for sending
                        layout.buttonSend.caption = "Send Now";
                    }

                    layout.buttonCancel.visible = false;

                    // For manual tenders, use a hybrid approach:
                    // Keep the layout's validation but override the send button behavior
                    if (!isAuto) {
                        // Keep the layout's click listener disabled to prevent the original behavior
                        layout.useLayoutClickListener = false;

                        // Clear any existing click listeners first to prevent duplicates
                        this.clearButtonListeners(layout.buttonSend);

                        // Override the send button click to use our own logic
                        layout.buttonSend.addClickListener(async () => {
                            // Prevent duplicate sends
                            if (this.manualTenderAlreadySent || layout.buttonSend.busy) {
                                return;
                            }

                            // Use the layout's validation
                            if (!(layout.textboxMustRespondBy.validateSimple() &&
                                  layout.textboxAddContact.validateSimple(false, true) &&
                                  layout.textboxContact.validateSimple(false, true))) {
                                return; // Don't proceed if validation fails
                            }

                            // Set flags to prevent duplicate processing
                            this.manualTenderAlreadySent = true;
                            layout.buttonSend.busy = true;

                            try {
                                const moveId = layout.mainDataSource.activeRow.get("moveid_out");
                                const carrierId = layout.labelCarrierId.text;
                                const purpose = layout.labelPurpose.text;
                                const mrb = layout.textboxMustRespondBy.text;
                                const contacts = layout.textboxContact.text as string;
                                const addlContacts = layout.textboxAddContact.text;

                                let allContacts: Array<string>;
                                let cString = "";

                                if (!StringUtil.isEmptyString(contacts)) {
                                    allContacts = contacts.split(",");
                                    for (let i = 0; i < allContacts.length; i++) {
                                        if (i > 0) cString += ",";
                                        cString += allContacts[i];
                                    }
                                }

                                if (!StringUtil.isEmptyString(addlContacts)) {
                                    const addContactList: string[] = addlContacts.split(",");
                                    addContactList.forEach((item) => {
                                        if (!StringUtil.isEmptyString(cString)) cString += ",";
                                        cString += item as string;
                                    });
                                }

                                const response = await Api.post("lme/datafusion/send-tender", {
                                    carrier: carrierId,
                                    move_id: moveId,
                                    respond_by: mrb,
                                    purpose: purpose,
                                    contacts: cString
                                });

                                // Update step caption to show results are now displayed
                                const rcStep = this.stepper.findComponentById("sendRateConStep") as Step;
                                rcStep.caption = "Tender Results";
                                layout.textboxContact.enabled = false;
                                layout.textboxAddContact.enabled = false;
                                layout.textboxMustRespondBy.enabled = false;

                                if (response.data[0]?.["tendersent"]) {
                                    // Update the purpose label to show sent status
                                    layout.labelPurpose.color = "success";
                                    layout.labelPurpose.dataSource = null;
                                    layout.labelPurpose.text = purpose + " (Sent in batch " + response.data[0].batchid + ")";
                                } else {
                                    // Update the purpose label to show error status
                                    layout.labelPurpose.color = "error";
                                    layout.labelPurpose.dataSource = null;
                                    layout.labelPurpose.text = purpose + " (" + (response.data[0].text || "Unable to transmit") + ")";
                                    Snackbar.showWarningSnackbar("Unable to send " + purpose + " tender for movement " + moveId + ".  " + response.data[0].text);
                                }

                                // Configure button as Next/Close for both success and failure cases
                                this.configureButtonAsNext(layout);

                            } catch (error) {
                                const moveId = layout.mainDataSource.activeRow.get("moveid_out");
                                const purpose = layout.labelPurpose.text;

                                // Update the purpose label to show error status
                                layout.labelPurpose.color = "error";
                                layout.labelPurpose.dataSource = null;
                                layout.labelPurpose.text = purpose + " (System error occurred)";

                                Snackbar.showWarningSnackbar("Unable to send " + purpose + " tender for movement " + moveId + ".  " + error);

                                // Configure button as Next/Close
                                this.configureButtonAsNext(layout);
                            } finally {
                                layout.buttonSend.busy = false;
                            }
                        });
                    }
                });

                layout.buttonSendLater.addClickListener(() => {
                    // Prevent interference if manual tender was already sent
                    if (this.manualTenderAlreadySent) {
                        return;
                    }

                    const isbrokerage = layout.activeRow.get("brokerage");
                    if(isbrokerage == "N") {
                        layout.activeRow.set("brokerage", "Y");
                        layout.activeRow.post();
                    }
                    // Advance to next step after clicking Send Later
                    this.doAfterTenderSentStep();
                });
                this.getStep(INDEX_RATE_CON).removeAll();
                this.getStep(INDEX_RATE_CON).add(layout);
                this.sendLoadTenderLayout = layout;
                resolve(this.sendLoadTenderLayout);
            });
        });
    }

    doAfterTenderSentStep(buttonSendLater?: Button) {
        if (this.manualTrackingRequired) {
            this.setupTrackingLayout().then(() => {
                this.advanceToNextStep();
            })
        } else {
            // No tracking required, use advanceToNextStep which will properly close if no more steps
            this.advanceToNextStep();
        }
    }

    /**
     * Helper method to clear all click listeners from a button
     */
    private clearButtonListeners(button: Button): void {
        const listeners = button.getListeners("_clickListeners")?.listeners || [];
        for (const listener of listeners) {
            button.removeClickListener(listener);
        }
    }

    /**
     * Helper method to set up the send button as a Next/Close button after tender processing
     */
    private configureButtonAsNext(layout: SendLoadTender): void {
        // Clear all existing listeners from both buttons
        this.clearButtonListeners(layout.buttonSend);
        this.clearButtonListeners(layout.buttonSendLater);

        // Hide Send Later button
        layout.buttonSendLater.visible = false;

        // Configure Send button as Next/Close
        layout.buttonSend.caption = this.manualTrackingRequired ? "Next" : "Close";
        layout.buttonSend.addClickListener(() => {
            this.doAfterTenderSentStep();
        });
    }

    private async handleAutoDataFusionCarrier(carrierId: string, rcStep: Step) {
        this.isAutoTenderInProgress = true; // Set flag to prevent early layout modifications
        rcStep.caption = "Tender Results";
        let batch = "0";
        let wasSuccessful = true;
        let errorText = "";

        // Prevent the framework from automatically resetting button state during async operations
        this.carrierAssignmentLayout.savebuttonAssign.setProps({
            busy: true,
            enabled: false,
            busyWhenDataSourceBusy: false,
            dataSource: null  // Disconnect from data source completely
        });

        this.assignedToastPanel = this.getAssignedToastPanel(
            this.carrierAssignmentLayout.mainDataSource.activeRow.get("override_payee_id"),
            this.carrierAssignmentLayout.mainDataSource.activeRow.getFirstLookupModelData("override_payee_id").get("name")
        );

        try {
            const result = await CarrierAssignment.sendAutoTender(carrierId, this.movementId);
            const tenderResult = result.data[0];

            if (tenderResult.tendersent) {
                batch = tenderResult.batchid;

                // Setup tender layout immediately after tender is sent
                await this.setupLoadTenderLayout(carrierId, this.movementId, true, batch, wasSuccessful, errorText);
                this.isAutoTenderInProgress = false; // Clear flag after we're done

                setTimeout(() => {
                    this.advanceToNextStep();
                }, 2000);
            } else {
                wasSuccessful = false;
                errorText = tenderResult.text || "Unable to transmit";
                Snackbar.showWarningSnackbar(
                    "Failed to send outbound tender to DataFusion carrier " +
                    carrierId + ": " + tenderResult.text,
                    { targetPanel: this.parent as Panel }
                );

                // Setup tender layout immediately after error
                await this.setupLoadTenderLayout(carrierId, this.movementId, true, batch, wasSuccessful, errorText);
                this.isAutoTenderInProgress = false; // Clear flag after we're done

                // Delay advancement to allow user to see error snackbar
                setTimeout(() => {
                    this.advanceToNextStep();
                }, 3000);
            }
        } catch (error) {
            this.isAutoTenderInProgress = false; // Clear flag on error
            log.error("Error in auto tender process:", error);
            Snackbar.showWarningSnackbar("Failed to send auto tender. Please try again.",
                { targetPanel: this.parent as Panel });

            // Setup tender layout immediately after system error
            await this.setupLoadTenderLayout(carrierId, this.movementId, true, "0", false, "System error occurred");

            // Delay advancement to allow user to see error snackbar
            setTimeout(() => {
                this.advanceToNextStep();
            }, 3000);
        }
    }

    private async handleManualDataFusionCarrier(carrierId: string, rcStep: Step) {
        rcStep.caption = "Send Tender";

        // Reset the manual tender flag for this new tender
        this.manualTenderAlreadySent = false;

        try {
            this.assignedToastPanel = this.getAssignedToastPanel(
                this.carrierAssignmentLayout.mainDataSource.activeRow.get("override_payee_id"),
                this.carrierAssignmentLayout.mainDataSource.activeRow.getFirstLookupModelData("override_payee_id").get("name")
            );

            await this.setupLoadTenderLayout(carrierId, this.movementId, false, "0");
            this.modifyLayoutAfterAssignment(this.carrierAssignmentLayout);
            this.advanceToNextStep();
        } catch (error) {
            log.error("Error in manual tender setup:", error);
            Snackbar.showWarningSnackbar("Failed to setup manual tender. Please try again.");
        }
    }

    private async handleStandardCarrier(rcStep: Step) {
        rcStep.caption = "Send Rate Con";

        try {
            await this.setupRateConLayout();
            this.modifyLayoutAfterAssignment(this.carrierAssignmentLayout);
            this.advanceToNextStep();
        } catch (error) {
            log.error("Error in rate con setup:", error);
            Snackbar.showWarningSnackbar("Failed to setup rate confirmation. Please try again.");
        }
    }

    public get carrierAssignmentLayout(): CarrierAssignment {
        return this._carrierAssignmentLayout;
    }

    public set carrierAssignmentLayout(value: CarrierAssignment) {
        this._carrierAssignmentLayout = value;
    }

    public get isIntercompany(): boolean {
        return this._isIntercompany;
    }

    public set isIntercompany(value: boolean) {
        this._isIntercompany = value;
    }

    public get carrierId(): string {
        return this._carrierId;
    }

    public set carrierId(value: string) {
        this._carrierId = value;
    }
}
